/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { WHITELABEL_MODE } from '@/lib/whitelabel';

/**
 * useWebRTC — peer-to-peer WebRTC video hook with WebSocket signaling.
 *
 * Zero-cost alternative to Twilio. Uses Google STUN + free TURN fallback for
 * NAT traversal. Media flows directly browser↔browser (no server relay).
 *
 * Backend signaling: /api/ws/video/{appointmentId}?token=<JWT>
 * See /app/backend/video_ws.py for the protocol.
 *
 * Usage:
 *   const {
 *     connected, connecting, error, remoteConnected, peerName,
 *     localVideoRef, remoteVideoRef, remoteAudioRef,
 *     connect, disconnect, toggleAudio, toggleVideo,
 *   } = useWebRTC({ appointmentId, autoConnect, camOn, micOn, backendUrl, token });
 */

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  // Free public TURN from OpenRelay Project — helps NAT-restrictive networks
  {
    urls: [
      'turn:openrelay.metered.ca:80',
      'turn:openrelay.metered.ca:443',
      'turn:openrelay.metered.ca:443?transport=tcp',
    ],
    username: 'openrelayproject',
    credential: 'openrelayproject',
  },
];

const wsFromHttp = (url) => url.replace(/^http/, 'ws');

export const useWebRTC = ({
  appointmentId,
  autoConnect = false,
  camOn = true,
  micOn = true,
  backendUrl,
  token,
} = {}) => {
  const [connected, setConnected] = useState(false);   // WebSocket signaling
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [peerName, setPeerName] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // Snapshot dei valori "volatili" letti da connect(): evita di inserirli fra le
  // dipendenze del useCallback (riconnessioni spurie ad ogni toggle mic/cam).
  const liveRef = useRef({ connecting: false, connected: false, micOn, camOn });
  liveRef.current = { connecting, connected, micOn, camOn };

  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const selfIdRef = useRef(null);
  const peerIdRef = useRef(null);
  const isCallerRef = useRef(false);
  const pendingCandidatesRef = useRef([]);
  const disposedRef = useRef(false);

  const cleanup = useCallback(() => {
    try { pcRef.current && pcRef.current.close(); }
    catch (e) { console.debug('useWebRTC.cleanup: pc.close() failed', e); }
    pcRef.current = null;
    try { wsRef.current && wsRef.current.close(); }
    catch (e) { console.debug('useWebRTC.cleanup: ws.close() failed', e); }
    wsRef.current = null;
    try {
      (localStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
    } catch (e) { console.debug('useWebRTC.cleanup: track.stop() failed', e); }
    localStreamRef.current = null;
    peerIdRef.current = null;
    setRemoteConnected(false);
    setPeerName(null);
    setConnected(false);
  }, []);

  const disconnect = useCallback(() => {
    disposedRef.current = true;
    try { wsRef.current && wsRef.current.readyState === 1 && wsRef.current.send(JSON.stringify({ type: 'leave' })); }
    catch (e) { console.debug('useWebRTC.disconnect: send(leave) failed', e); }
    cleanup();
  }, [cleanup]);

  const attachLocalStream = useCallback((stream) => {
    localStreamRef.current = stream;
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }
  }, []);

  const attachRemoteStream = useCallback((stream) => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = stream;
    }
    setRemoteConnected(true);
  }, []);

  const createPeerConnection = useCallback(() => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (e) => {
      if (e.candidate && peerIdRef.current && wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({
          type: 'ice',
          to: peerIdRef.current,
          candidate: e.candidate.toJSON ? e.candidate.toJSON() : e.candidate,
        }));
      }
    };
    pc.ontrack = (e) => {
      const stream = e.streams && e.streams[0] ? e.streams[0] : new MediaStream([e.track]);
      attachRemoteStream(stream);
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'disconnected', 'closed'].includes(pc.connectionState)) {
        setRemoteConnected(false);
      }
    };
    return pc;
  }, [attachRemoteStream]);

  const drainPendingCandidates = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.remoteDescription) return;
    const list = pendingCandidatesRef.current;
    pendingCandidatesRef.current = [];
    for (const c of list) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); }
      catch (e) { console.debug('useWebRTC: pending addIceCandidate failed', e); }
    }
  }, []);

  const handleMessage = useCallback(async (msg) => {
    if (!msg || !msg.type) return;
    const pc = pcRef.current;

    if (msg.type === 'ready') {
      selfIdRef.current = msg.self_id;
      // If peers already present, we become the CALLER (send offer)
      const other = (msg.peers || [])[0];
      if (other) {
        peerIdRef.current = other.peer_id;
        setPeerName(other.name);
        isCallerRef.current = true;
        try {
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
          await pc.setLocalDescription(offer);
          wsRef.current.send(JSON.stringify({
            type: 'offer', to: other.peer_id, sdp: offer,
          }));
        } catch (e) {
          setError('Errore avvio chiamata: ' + (e.message || 'unknown'));
        }
      }
    } else if (msg.type === 'peer-joined') {
      // We were waiting — remote just entered. If we're not caller, wait for their offer.
      peerIdRef.current = msg.peer_id;
      setPeerName(msg.name);
      // If we were caller (already sent offer earlier), do nothing; renegotiation happens on new offer.
    } else if (msg.type === 'peer-left') {
      setRemoteConnected(false);
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      peerIdRef.current = null;
    } else if (msg.type === 'offer') {
      peerIdRef.current = msg.from;
      isCallerRef.current = false;
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        await drainPendingCandidates();
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        wsRef.current.send(JSON.stringify({ type: 'answer', to: msg.from, sdp: answer }));
      } catch (e) {
        setError('Errore risposta chiamata: ' + (e.message || 'unknown'));
      }
    } else if (msg.type === 'answer') {
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        await drainPendingCandidates();
      } catch (e) {
        setError('Errore ricezione risposta: ' + (e.message || 'unknown'));
      }
    } else if (msg.type === 'ice') {
      if (!pc.remoteDescription) {
        pendingCandidatesRef.current.push(msg.candidate);
      } else {
        try { await pc.addIceCandidate(new RTCIceCandidate(msg.candidate)); }
        catch (e) { console.debug('useWebRTC: live addIceCandidate failed', e); }
      }
    }
  }, [drainPendingCandidates]);

  const connect = useCallback(async () => {
    if (!appointmentId) return;
    if (liveRef.current.connecting || liveRef.current.connected) return;
    disposedRef.current = false;
    setConnecting(true);
    setError(null);

    // -------- WHITELABEL DEMO MODE ----------------------------------------
    // No signaling server, no peer. We attach the local camera stream to the
    // local video element AND clone it into the remote video slot so the UI
    // looks alive. Perfect for demo / kiosk / offline clinic deployments.
    if (WHITELABEL_MODE) {
      try {
        const constraints = {
          audio: liveRef.current.micOn ? { echoCancellation: true, noiseSuppression: true } : false,
          video: liveRef.current.camOn ? { width: 640, height: 480, facingMode: 'user' } : false,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        attachLocalStream(stream);
        // Loop the same stream to the "remote" video so the doctor can present.
        if (remoteVideoRef.current) {
          try {
            remoteVideoRef.current.srcObject = stream;
            remoteVideoRef.current.muted = true; // avoid echo
            remoteVideoRef.current.play?.().catch(() => {});
          } catch (e) { /* ignore */ }
        }
        setPeerName('Modalità dimostrativa');
        setConnected(true);
        setRemoteConnected(true);
        setConnecting(false);
        return;
      } catch (e) {
        setError(e.message || 'Errore accesso camera/microfono');
        setConnecting(false);
        return;
      }
    }

    // -------- LIVE MODE (original signaling behaviour) --------------------
    if (!token || !backendUrl) {
      setConnecting(false);
      return;
    }
    try {
      // 1. Get local media
      const constraints = {
        audio: liveRef.current.micOn ? { echoCancellation: true, noiseSuppression: true } : false,
        video: liveRef.current.camOn ? { width: 640, height: 480, facingMode: 'user' } : false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      attachLocalStream(stream);

      // 2. Build peer connection & attach tracks
      const pc = createPeerConnection();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      pcRef.current = pc;

      // 3. Open signaling WebSocket
      const wsUrl = `${wsFromHttp(backendUrl)}/api/ws/video/${encodeURIComponent(appointmentId)}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        setConnecting(false);
      };
      ws.onmessage = async (evt) => {
        if (evt.data === 'pong') return;
        try {
          const msg = JSON.parse(evt.data);
          await handleMessage(msg);
        } catch (e) {
          console.debug('useWebRTC: ws.onmessage parse/handle failed', e);
        }
      };
      ws.onerror = () => {
        setError('Errore connessione signaling');
      };
      ws.onclose = () => {
        setConnected(false);
        if (!disposedRef.current) {
          cleanup();
        }
      };
    } catch (e) {
      setError(e.message || 'Errore accesso camera/microfono');
      setConnecting(false);
      cleanup();
    }
  }, [appointmentId, token, backendUrl, attachLocalStream, createPeerConnection, handleMessage, cleanup]);

  // Auto-connect: keep the connect function in a ref so the effect only fires
  // when the "trigger" inputs change (autoConnect / appointmentId / token / backendUrl).
  // This avoids stale closures without pulling `connect` (and all its deps) into
  // the dependency array, which would cause reconnect storms on every mic/cam toggle.
  const connectRef = useRef(connect);
  useEffect(() => { connectRef.current = connect; }, [connect]);
  useEffect(() => {
    if (autoConnect && !connected && !connecting && !error) {
      connectRef.current();
    }
  }, [autoConnect, appointmentId, token, backendUrl, connected, connecting, error]);

  // Enable/disable tracks live
  useEffect(() => {
    if (!localStreamRef.current) return;
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !!micOn; });
    localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = !!camOn; });
  }, [micOn, camOn]);

  // Cleanup on unmount
  useEffect(() => () => { disposedRef.current = true; cleanup(); }, [cleanup]);

  return {
    connected,
    connecting,
    error,
    remoteConnected,
    peerName,
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    connect,
    disconnect,
  };
};
